<template>
  <div class="course-carousel">
    <div class="course-carousel__link">
      <h3 class="course-carousel__link-title">{{ customTitle || tag }}</h3>
      <div
        v-if="tag"
        class="course-carousel__view-all"
        @click="searchTopic(tag)"
      >
        View All
        <span class="material-icons course-carousel__more-icon"
          >keyboard_arrow_right</span
        >
      </div>
    </div>
    <Swiper
      v-if="courses && courses.length"
      class="course-carousel__tile-wrapper"
      :parentClass="swiperClass"
      :swiperOptions="swiperOption"
    >
      <div
        v-for="course in courses"
        :key="course.objectID"
        class="swiper-slide course-carousel__tile"
        :style="
          `background-image: url(${$getCourseBackground(
            course.objectID,
            'small'
          )}), linear-gradient(${$getGradient()})`
        "
      >
        <div @click="expandCourse(course)" class="course-carousel__course-link">
          <div class="course-carousel__overlay"></div>

          <div class="course-carousel__details">
            <div
              v-if="course.has_sample_content && isFreeTrialUser"
              class="course-carousel__snipe"
            >
              Included
            </div>
            <div
              v-if="course.status === 'coming soon'"
              class="course-carousel__snipe"
            >
              Coming Soon
            </div>
            <div
              class="course-carousel__title"
              v-tooltip="{
                content: course.name.length >= maxLength ? course.name : '',
              }"
            >
              {{ truncateTitle(course.name) }}
            </div>
            <div class="course-carousel__presenter">
              <div
                v-if="course.instructors[0]"
                class="course-carousel__name-wrapper"
              >
                <p class="course-carousel__lead">Instructor</p>
                <p class="name">
                  {{ `${course.instructors[0].name}` }}
                </p>
              </div>
              <div class="course-carousel__branding">
                {{
                  course.learning_areas && course.learning_areas[0]
                    ? course.learning_areas[0].name
                    : course.vendor
                }}
              </div>
            </div>
            <div class="course-carousel__tile-arrow">
              <i class="material-icons">keyboard_arrow_down</i>
            </div>
          </div>
        </div>
      </div>
    </Swiper>
    <CourseCarouselExpanded
      v-if="expandedCourse"
      @close="closeExpanded"
      @searchPresenter="searchPresenter"
      :course="expandedCourse"
      :presenter="expandedCourse.instructors[0]"
      :duration="$duration.getCourseDuration(expandedCourse)"
    />
  </div>
</template>

<script>
import Swiper from '@/components/layout/Swiper.vue'
import { createIndices } from '@/services/search'
import CourseCarouselExpanded from '@/components/home/subscriber-view/CourseCarouselExpanded.vue'
import { truncateText } from '@/helpers'

export default {
  name: 'CourseCarousel',
  components: {
    Swiper,
    CourseCarouselExpanded,
  },
  props: {
    algoliaKeys: {
      type: Object,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
    tag: {
      type: String,
      required: true,
    },
    facet: {
      type: String,
      required: false,
      default: () => {
        return 'tags.name'
      },
    },
    customTitle: {
      type: String,
      required: false,
    },
    swiperClass: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      swiperOption: {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        slidesPerColumn: 1,
        spaceBetween: 10,
        freeMode: true,
        loop: false,
      },
      courses: [],
      expandedCourse: '',
      maxLength: 85,
    }
  },
  methods: {
    truncateTitle(value) {
      return truncateText(value, this.maxLength)
    },
    expandCourse(course) {
      this.expandedCourse = course
    },
    closeExpanded() {
      this.expandedCourse = ''
    },
    searchTopic(query) {
      this.$store.dispatch('search/setQuery', query)
      this.$router.push({ name: 'search' })
    },
    searchPresenter(name) {
      this.$store.dispatch('search/setQuery', '')
      this.$router.push({ name: 'search', query: { Instructors: name } })
    },
    handleResults(index, { hits }) {
      this.courses = hits
    },
    createSearchInstance() {
      const {
        algoliaKeys: { appKey, appId, coursesIndex: courses },
      } = this
      const setup = {
        keys: {
          appId,
          appKey,
        },
        contexts: [
          {
            name: 'Courses',
            index: courses,
            facets: [this.facet],
          },
        ],
        resultsHandler: this.handleResults,
      }
      const parentFilter = {
        facet: this.facet,
        value: this.tag,
      }
      createIndices(setup, parentFilter)
    },
  },
  created() {
    this.createSearchInstance()
  },
}
</script>

<style lang="scss">
.course-carousel {
  margin-bottom: 80px;
  &__link {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__link-title {
    color: var(--white);
    font-size: 1.1rem;
    padding: 0;
    margin: 0;
  }
  &__view-all {
    color: var(--white);
    padding: 5px 20px;
    display: flex;
    white-space: nowrap;
    align-items: center;
    justify-content: space-between;
    transition: 0.5s all ease;
    text-decoration: none;
    &:hover {
      cursor: pointer;
      color: var(--orange);
      text-decoration: none;
    }
  }
  &__more-icon {
    font-size: 1.5rem;
    color: var(--orange);
    transition: 0.5s all ease;
  }
  &__tile-wrapper {
    padding: 20px;
    background-color: var(--light-gray);
    height: 250px;
  }
  &__tile {
    text-align: left;
    width: 375px !important;
    color: var(--white);
    background-color: var(--orange);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 1;
    transition: 0.5s all ease;
    @media screen and (max-width: 768px) {
      width: 350px !important;
    }
    &:hover {
      cursor: pointer;
      padding-top: 25px;
      margin-top: -20px;
      padding-bottom: 25px;
      .course-carousel__tile-arrow {
        opacity: 1;
      }
      .course-expander {
        opacity: 1;
      }
    }
  }
  &__course-link {
    text-decoration: none;
    color: var(--white);
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    &:hover {
      color: var(--white);
      text-decoration: none;
      .course-carousel__tile-overlay {
        opacity: 1;
      }
    }
  }
  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    right: 0;
    top: 0;
    z-index: -1;
    opacity: 0;
    transition: 0.5s all ease;
  }
  &__details {
    padding: 20px;
    padding-top: 30px;
    padding-right: 10px;
    padding-bottom: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &__snipe {
    position: absolute;
    top: 0;
    right: 0;
    background: var(--black);
    color: var(--white);
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 700;
    padding: 7px 10px;
  }
  &__title {
    font-size: 1.2rem;
    font-weight: 700;
    max-width: 270px;
  }
  &__presenter {
    display: flex;
    justify-content: space-between;
  }
  &__name-wrapper {
    padding-bottom: 20px;
  }
  &__lead {
    color: lightgrey;
    text-transform: uppercase;
    margin: 0;
    font-size: 0.8rem;
    font-weight: 500;
  }
  &__name {
    color: var(--white);
    font-weight: 700;
    margin: 0;
  }
  &__branding {
    font-weight: 700;
    font-size: 0.8rem;
    text-transform: uppercase;
    align-self: flex-end;
    padding-bottom: 15px;
    max-width: 100px;
  }
  &__tile-arrow {
    text-align: center;
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    opacity: 0;
    transition: opacity 0.7s ease;
    i {
      font-size: 32px;
    }
  }
  .swiper-button-disabled {
    display: none;
  }
}
</style>
